import { MetadataFormData } from "@saleor/components/Metadata/types";
import { MultiAutocompleteChoiceType } from "@saleor/components/MultiAutocompleteSelectField";
import { SingleAutocompleteChoiceType } from "@saleor/components/SingleAutocompleteSelectField";
import { ProductVariant } from "@saleor/fragments/types/ProductVariant";
import { FormsetAtomicData } from "@saleor/hooks/useFormset";
import { maybe, splitDateTime } from "@saleor/misc";
import {
  ProductDetails_product,
  ProductDetails_product_collections,
  ProductDetails_product_variants
} from "@saleor/products/types/ProductDetails";
import { SearchProductTypes_search_edges_node_productAttributes } from "@saleor/searches/types/SearchProductTypes";
import { ProductLensTypeTemplate, StockInput } from "@saleor/types/globalTypes";
import { mapMetadataItemToInput } from "@saleor/utils/maps";

// import { RawDraftContentState } from "draft-js";
import { ProductAttributeInput } from "../components/ProductAttributes";
import { SkuAttributeInput } from "../components/ProductSkuAttributes";
import { ProductStockInput } from "../components/ProductStocks";
import { VariantAttributeInput } from "../components/ProductVariantAttributes";
import { GetErpProductVariant_erpProductVariant } from "../types/GetErpProductVariant";
import { ProductVariantCreateData_product } from "../types/ProductVariantCreateData";

export interface Collection {
  id: string;
  label: string;
}

interface Node {
  id: string;
  name: string;
}

export interface ProductType {
  hasVariants: boolean;
  id: string;
  name: string;
  productAttributes: SearchProductTypes_search_edges_node_productAttributes[];
}

export function getAttributeInputFromProduct(
  product: ProductDetails_product
): ProductAttributeInput[] {
  return maybe(
    (): ProductAttributeInput[] =>
      product.attributes.map(attribute => ({
        data: {
          inputType: attribute.attribute.inputType,
          isRequired: attribute.attribute.valueRequired,
          values: attribute.attribute.values
        },
        id: attribute.attribute.id,
        label: attribute.attribute.name,
        value: attribute.values.map(value => value.name)
      })),
    []
  );
}

export interface ProductAttributeValueChoices {
  id: string;
  values: MultiAutocompleteChoiceType[];
}
export function getSelectedAttributesFromProduct(
  product: ProductDetails_product
): ProductAttributeValueChoices[] {
  return maybe(
    () =>
      product.attributes.map(attribute => ({
        id: attribute.attribute.id,
        values: attribute.values.map(value => ({
          label: value.name,
          value: value.name
        }))
      })),
    []
  );
}

export function getAttributeInputFromProductType(
  productType: ProductType
): ProductAttributeInput[] {
  return productType.productAttributes.map(attribute => ({
    data: {
      inputType: attribute.inputType,
      isRequired: attribute.valueRequired,
      values: attribute.values
    },
    id: attribute.id,
    label: attribute.name,
    value: []
  }));
}

export function getAttributeInputFromVariant(
  variant: ProductVariant,
  erpProductVariant: GetErpProductVariant_erpProductVariant
): VariantAttributeInput[] {
  const erpAttributes = erpProductVariant?.attributes || [];
  return maybe(
    (): VariantAttributeInput[] =>
      variant.attributes.map(attribute => {
        const erpAttribute = erpAttributes
          ? erpAttributes?.find(
              erpAttr =>
                attribute.attribute.name.toLowerCase() ===
                erpAttr.name.toLowerCase()
            )
          : null;
        return {
          data: {
            values: attribute.attribute.values
          },
          id: attribute.attribute.id,
          label: attribute.attribute.name,
          value: maybe(
            () => erpAttribute?.value || attribute.values[0].name,
            null
          )
        };
      }),
    []
  );
}
export function getAttributeInputFromSku(
  variant: ProductVariant,
  erpProductVariant: GetErpProductVariant_erpProductVariant
): SkuAttributeInput[] {
  const erpAttributes = erpProductVariant?.attributes || [];
  return maybe(
    (): SkuAttributeInput[] =>
      variant.skuAttributes.map(attribute => {
        const erpAttribute = erpAttributes
          ? erpAttributes?.find(
              erpAttr =>
                attribute.attribute.name.toLowerCase() ===
                erpAttr.name.toLowerCase()
            )
          : null;
        return {
          data: {
            values: attribute.attribute.values
          },
          id: attribute.attribute.id,
          label: attribute.attribute.name,
          value: maybe(
            () => erpAttribute?.value || attribute.values[0].name,
            null
          )
        };
      }),
    []
  );
}
export function getStockInputFromVariant(
  variant: ProductVariant
): ProductStockInput[] {
  return (
    variant?.stocks.map(stock => ({
      data: null,
      id: stock.warehouse.id,
      label: stock.warehouse.name,
      value: stock.quantity.toString()
    })) || []
  );
}

export function getVariantAttributeInputFromProduct(
  product: ProductVariantCreateData_product,
  erpProductVariant: GetErpProductVariant_erpProductVariant
): VariantAttributeInput[] {
  const erpAttributes = erpProductVariant?.attributes || [];
  return product?.productType?.variantAttributes?.map(attribute => {
    const erpAttribute = erpAttributes
      ? erpAttributes?.find(
          erpAttr => attribute.name.toLowerCase() === erpAttr.name.toLowerCase()
        )
      : null;
    return {
      data: {
        values: attribute.values
      },
      id: attribute.id,
      label: attribute.name,
      value: erpAttribute?.value || ""
    };
  });
}

export function getStockInputFromProduct(
  product: ProductDetails_product
): ProductStockInput[] {
  return maybe(
    () =>
      product?.variants[0]?.stocks.map(stock => ({
        data: null,
        id: stock.warehouse.id,
        label: stock.warehouse.name,
        value: stock.quantity.toString()
      })),
    []
  );
}

export function getCollectionInput(
  productCollections: ProductDetails_product_collections[]
): Collection[] {
  return maybe(
    () =>
      productCollections.map(collection => ({
        id: collection.id,
        label: collection.name
      })),
    []
  );
}

export function getChoices(nodes: Node[]): SingleAutocompleteChoiceType[] {
  return maybe(
    () =>
      nodes.map(node => ({
        label: node.name,
        value: node.id
      })),
    []
  );
}

export interface ProductUpdatePageFormData extends MetadataFormData {
  availableForPurchaseDate: string;
  availableForPurchaseTime: string;
  basePrice: number;
  category: string | null;
  changeTaxCode: boolean;
  chargeTaxes: boolean;
  collections: string[];
  description: string;
  descriptionJson: string;
  USP: string;
  care: string;
  isAvailable: boolean;
  isAvailableForPurchase: boolean;
  isPublished: boolean;
  isPreOrder: boolean;
  preOrderDate: string;
  preOrderTime: string;
  name: string;
  slug: string;
  publicationDate: string;
  seoDescription: string;
  seoTitle: string;
  seoKeyword: string;
  sku: string;
  taxCode: string;
  trackInventory: boolean;
  visibleInListings: boolean;
  weight: string;
  lensTypeTemplate: any;
}

export function getProductUpdatePageFormData(
  product: ProductDetails_product,
  variants: ProductDetails_product_variants[]
): ProductUpdatePageFormData {
  return {
    availableForPurchaseDate: splitDateTime(
      maybe(() => product?.availableForPurchase, "")
    ).date,
    availableForPurchaseTime: splitDateTime(
      maybe(() => product?.availableForPurchase, "")
    ).time,
    basePrice: maybe(() => product.variants[0].price.amount, 0),
    category: maybe(() => product.category.id, ""),
    changeTaxCode: !!product?.taxType.taxCode,
    chargeTaxes: maybe(() => product.chargeTaxes, false),
    collections: maybe(
      () => product.collections.map(collection => collection.id),
      []
    ),
    descriptionJson: maybe(
      () => product.descriptionJson,
      '{"Tip": "N", "Care": "N"}'
    ),
    description: maybe(() => product.description, ""),
    USP: maybe(() => product.USP, ""),
    care: maybe(() => product.care, ""),
    isAvailable: !!product?.isAvailable,
    isAvailableForPurchase: !!product?.isAvailableForPurchase,
    isPreOrder: !!product?.isPreOrder,
    preOrderDate: splitDateTime(maybe(() => product?.preOrder, "")).date,
    preOrderTime: splitDateTime(maybe(() => product?.preOrder, "")).time,
    isPublished: maybe(() => product.isPublished, false),
    metadata: product?.metadata?.map(mapMetadataItemToInput),
    name: maybe(() => product.name, ""),
    privateMetadata: product?.privateMetadata?.map(mapMetadataItemToInput),
    publicationDate: maybe(() => product.publicationDate, ""),
    seoDescription: maybe(() => product.seoDescription, ""),
    seoTitle: maybe(() => product.seoTitle, ""),
    seoKeyword: maybe(() => product.seoKeyword, ""),
    sku: maybe(
      () =>
        product.productType.hasVariants
          ? undefined
          : variants && variants[0]
          ? variants[0].sku
          : undefined,
      ""
    ),
    slug: product?.slug || "",
    taxCode: product?.taxType.taxCode,
    trackInventory: maybe(() => product.variants[0].trackInventory, false),
    visibleInListings: !!product?.visibleInListings,
    weight: product?.weight?.value.toString() || "",
    lensTypeTemplate: maybe(
      () => product.lensTypeTemplate,
      ProductLensTypeTemplate.CLASSIC
    )
  };
}

export function mapFormsetStockToStockInput(
  stock: FormsetAtomicData<null, string>
): StockInput {
  return {
    quantity: parseInt(stock.value, 10) || 0,
    warehouse: stock.id
  };
}
